/**
 * Centralized React Query hooks for the OS data layer.
 *
 * Why this exists:
 *  - Today every OS page does `useState([]) + useEffect(() => db().listX())`
 *    which forces a full refetch on every navigation. No caching, no
 *    deduplication, no background revalidation.
 *  - These hooks wrap the existing `db()` calls in React Query so multiple
 *    components/pages share a single in-memory cache, navigation feels
 *    instant, and stale data is silently refreshed in the background.
 *
 * Design notes:
 *  - The query key always includes the active env (`sandbox` | `cloud`) so
 *    flipping the env toggle does NOT mix cached results between modes.
 *  - Hooks are pure additions — pages that haven't migrated yet keep
 *    working exactly as before (they simply don't subscribe to this cache).
 *  - For mutations, call `invalidateOS(qc, env, "projects")` after
 *    writing — it invalidates the matching cached list for the active env.
 */
import { useQuery, useQueryClient, type QueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useEnvStore, type OsEnv } from "@/os/env/useEnvStore";
import { db } from "@/os/data";
import type {
  ClientRow,
  Invoice,
  Payment,
  PaymentMethod,
  ProjectRow,
  Quote,
  Resource,
  TeamProfile,
  UserWithRoles,
} from "@/os/data/types";

// ---------- key helpers ----------

/** Build a query key scoped to the active env. */
export const osKey = (env: OsEnv | null, ...parts: readonly unknown[]) =>
  ["os", env ?? "unknown", ...parts] as const;

/** Invalidate every cached query for a given entity in the active env. */
export function invalidateOS(qc: QueryClient, env: OsEnv | null, ...parts: readonly unknown[]) {
  return qc.invalidateQueries({ queryKey: osKey(env, ...parts) });
}

/** Invalidate the entity across both envs (rarely needed). */
export function invalidateOSAll(qc: QueryClient, ...parts: readonly unknown[]) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["os", "sandbox", ...parts] }),
    qc.invalidateQueries({ queryKey: ["os", "production", ...parts] }),
  ]);
}

// ---------- generic wrapper ----------

type OSQueryOptions<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn">;

/**
 * Generic OS-aware query hook. Auto-scopes the key to the active env so
 * sandbox and cloud data never mix in the cache.
 */
export function useOSQuery<T>(
  parts: readonly unknown[],
  fetcher: () => Promise<T>,
  options?: OSQueryOptions<T>,
) {
  const env = useEnvStore((s) => s.env);
  return useQuery<T, Error, T, readonly unknown[]>({
    queryKey: osKey(env, ...parts),
    queryFn: fetcher,
    ...options,
  });
}

/** Read-only helper for hooks that want the current env without re-subscribing internally. */
export const useOSEnv = () => useEnvStore((s) => s.env);

// ---------- entity hooks (most-reused across OS pages) ----------

export const useOSProjects = (options?: OSQueryOptions<ProjectRow[]>) =>
  useOSQuery<ProjectRow[]>(["projects"], () => db().listProjects(), options);

export const useOSClients = (options?: OSQueryOptions<ClientRow[]>) =>
  useOSQuery<ClientRow[]>(["clients"], () => db().listClients(), options);

export const useOSClientsLite = (
  options?: OSQueryOptions<Awaited<ReturnType<ReturnType<typeof db>["listClientsLite"]>>>,
) =>
  useOSQuery(["clients", "lite"], () => db().listClientsLite(), options);

export const useOSTeamProfiles = (options?: OSQueryOptions<TeamProfile[]>) =>
  useOSQuery<TeamProfile[]>(["team_profiles"], () => db().listTeamProfiles(), options);

export const useOSResources = (options?: OSQueryOptions<Resource[]>) =>
  useOSQuery<Resource[]>(["resources"], () => db().listResources(), options);

export const useOSInvoices = (options?: OSQueryOptions<Invoice[]>) =>
  useOSQuery<Invoice[]>(["invoices"], () => db().listInvoices(), options);

export const useOSPayments = (options?: OSQueryOptions<Payment[]>) =>
  useOSQuery<Payment[]>(["payments"], () => db().listPayments(), options);

export const useOSQuotes = (options?: OSQueryOptions<Quote[]>) =>
  useOSQuery<Quote[]>(["quotes"], () => db().listQuotes(), options);

export const useOSUsersWithRoles = (options?: OSQueryOptions<UserWithRoles[]>) =>
  useOSQuery<UserWithRoles[]>(["users_with_roles"], () => db().listUsersWithRoles(), options);

export const useOSPaymentMethods = (options?: OSQueryOptions<PaymentMethod[]>) =>
  useOSQuery<PaymentMethod[]>(["payment_methods"], () => db().listPaymentMethods(), options);

// ---------- per-user hooks ----------

export const useOSOperatorAssignedProjects = (
  userId: string,
  options?: OSQueryOptions<ProjectRow[]>,
) =>
  useOSQuery<ProjectRow[]>(
    ["operator_assigned_projects", userId],
    () => db().operatorAssignedProjects(userId) as Promise<ProjectRow[]>,
    { enabled: !!userId, ...options },
  );

export const useOSClientHubData = (
  userId: string,
  options?: OSQueryOptions<Awaited<ReturnType<ReturnType<typeof db>["clientHubData"]>>>,
) =>
  useOSQuery(
    ["client_hub_data", userId],
    () => db().clientHubData(userId),
    { enabled: !!userId, ...options },
  );

export const useOSClientInvoices = (
  userId: string,
  options?: OSQueryOptions<Awaited<ReturnType<ReturnType<typeof db>["clientInvoices"]>>>,
) =>
  useOSQuery(
    ["client_invoices", userId],
    () => db().clientInvoices(userId),
    { enabled: !!userId, ...options },
  );

export const useOSClientProjectDetail = (
  projectId: string | undefined,
  options?: OSQueryOptions<Awaited<ReturnType<ReturnType<typeof db>["clientProjectDetail"]>>>,
) =>
  useOSQuery(
    ["client_project_detail", projectId ?? ""],
    () => db().clientProjectDetail(projectId!),
    { enabled: !!projectId, ...options },
  );

// ---------- convenience re-exports ----------

export { useQueryClient };
export type { QueryClient };